import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';
import { PORTALS } from '../config/systems';
import { ipApi, isLiveApi } from '../services/api';
import {
  hasValidPlatformSession, clearPlatformSession, STORAGE_KEY, TOKEN_KEY,
} from './platformSession';

const PlatformAuthContext = createContext(null);

export function PlatformAuthProvider({ children }) {
  const [user, setUser] = useState(() => {
    if (!hasValidPlatformSession()) {
      clearPlatformSession();
      return null;
    }
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch {
      clearPlatformSession();
      return null;
    }
  });

  const login = useCallback(async (email, password) => {
    if (!isLiveApi()) throw new Error('Enable live API for platform admin');
    clearPlatformSession();
    const data = await ipApi.platformLogin(email, password);
    const session = {
      adminId: data.user.adminId,
      email: data.user.email,
      name: data.user.name,
      role: 'platform_admin',
      portal: PORTALS.NETWORKS_PLATFORM.id,
    };
    localStorage.setItem(TOKEN_KEY, data.token);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(session));
    setUser(session);
    return session;
  }, []);

  const logout = useCallback(() => {
    clearPlatformSession();
    setUser(null);
  }, []);

  const value = useMemo(
    () => ({ user, login, logout, isLoggedIn: !!user && hasValidPlatformSession(), portal: PORTALS.NETWORKS_PLATFORM }),
    [user, login, logout]
  );

  return <PlatformAuthContext.Provider value={value}>{children}</PlatformAuthContext.Provider>;
}

export function usePlatformAuth() {
  const ctx = useContext(PlatformAuthContext);
  if (!ctx) throw new Error('usePlatformAuth must be inside PlatformAuthProvider');
  return ctx;
}
